import type { GaVueComponent } from "@/common/vueUtils";
import { createParentComponent } from "@/common/vueUtils";
import { createStore, type Store } from "@/common/storeUtils";
import { GaButton } from "@/components/general/GaButton";
import "./GaModal.scss";
import { onKeyUp } from "@vueuse/core";
import { UseFocusTrap } from "@vueuse/integrations/useFocusTrap/component";
import { TransitionGroup } from "vue";
import { useGaContext } from "@utils/vue-migration/common/gaContext/gaContext";
import type { IconClass } from "@/common/iconUtils";

type ModalSize = "lg" | "xl" | "fullscreen";

export type ConfirmModalConfig = {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: "primary" | "danger";
};

export type CustomModalConfig = {
  title?: string;
  titleIcon?: IconClass;
  body: (fns: ModalFunctions) => GaVueComponent;
  footer?: (fns: ModalFunctions) => GaVueComponent;
  size?: ModalSize;
  clickOutsideDeactivates?: (e: MouseEvent | TouchEvent) => boolean;
};

type ModalFunctions = {
  close: () => void;
};

type GaModalState = {
  kind: "CONFIRM_MODAL";
  title: string | null;
  titleIcon: IconClass | null;
  body: (fns: ModalFunctions) => GaVueComponent;
  footer?: (fns: ModalFunctions) => GaVueComponent;
  close: () => void;
  size?: ModalSize;
  clickOutsideDeactivates?: (e: MouseEvent | TouchEvent) => boolean;
};

function afterEachChange(newState: GaModalState | null): void {
  const backdropClass = "modal-open";
  const body = document.querySelector("body");
  const compensation = window.innerWidth - document.documentElement.clientWidth;
  if (newState === null) {
    body?.removeAttribute("style");
    body?.classList.remove(backdropClass);
  } else {
    body?.setAttribute("style", `--scrollbar-compensation: ${compensation}px`);
    body?.classList.add(backdropClass);
  }
}

namespace GaModal {
  export function open(store: Store<GaModalState | null>, newState: GaModalState): void {
    store.set(newState);
    afterEachChange(newState);
  }

  export function close(store: Store<GaModalState | null>): void {
    store.set(null);
    afterEachChange(null);
  }
}

const store = createStore<GaModalState | null>(null);

export function useCustomModal(): (config: CustomModalConfig) => Promise<void> {
  return (config) => {
    return new Promise((resolve) => {
      function cancel(): void {
        GaModal.close(store);
        resolve();
      }

      const title = config.title === undefined || config.title.trim().length === 0 ? null : config.title;
      GaModal.open(store, {
        kind: "CONFIRM_MODAL",
        title: title,
        titleIcon: config.titleIcon ?? null,
        body: config.body,
        close: cancel,
        footer: config.footer,
        size: config.size,
        clickOutsideDeactivates: config.clickOutsideDeactivates,
      });
    });
  };
}

export function useConfirmationModal(): (config: ConfirmModalConfig) => Promise<boolean> {
  const { t } = useGaContext();
  return (config) => {
    return new Promise((resolve) => {
      const title = config.title === undefined || config.title.trim().length === 0 ? null : config.title;
      const cancelLabel = config.cancelLabel === undefined || config.cancelLabel.trim().length === 0 ? null : config.cancelLabel;
      const confirmLabel = config.confirmLabel === undefined || config.confirmLabel.trim().length === 0 ? null : config.confirmLabel;

      function cancel(): void {
        GaModal.close(store);
        resolve(false);
      }

      function confirm(): void {
        GaModal.close(store);
        resolve(true);
      }

      GaModal.open(store, {
        kind: "CONFIRM_MODAL",
        title,
        titleIcon: null,
        body: () => <div vHtml={config.message} />,
        footer: () => (
          <>
            <GaButton onClick={cancel} variant="primary-alt">
              {cancelLabel ?? t("general:button.cancel")}
            </GaButton>
            <GaButton onClick={confirm} variant={config.variant ?? "primary"}>
              {confirmLabel ?? t("general:button.confirm")}
            </GaButton>
          </>
        ),
        close: cancel,
      });
    });
  };
}

function getModalSizeClass(size: ModalSize): string {
  switch (size) {
    case "lg": {
      return "modal-lg";
    }
    case "xl": {
      return "modal-xl";
    }
    case "fullscreen": {
      return "modal-fullscreen";
    }
  }
}

function GaConfirmationModal(props: { state: GaModalState }): GaVueComponent {
  const labelId = "modalLiveLabel";
  const labelledby = props.state.title !== null ? labelId : undefined;

  onKeyUp("Escape", () => props.state.close());

  return (
    <div class="modal d-block" tabindex="-1" aria-modal="true" role="dialog" aria-labelledby={labelledby}>
      <div class={["modal-dialog", props.state.size !== undefined ? getModalSizeClass(props.state.size) : ""]}>
        <UseFocusTrap
          options={{
            clickOutsideDeactivates: (e) => {
              if (props.state.clickOutsideDeactivates !== undefined && props.state.clickOutsideDeactivates(e)) {
                return true;
              }
              props.state.close();
              return true;
            },
          }}>
          <div class="modal-content">
            <div class="modal-header">
              {props.state.title !== null ? (
                <h5 class="modal-title" id={labelId}>
                  {props.state.titleIcon === null || <i class={[props.state.titleIcon, "me-1"]}></i>}
                  {props.state.title}
                </h5>
              ) : null}
              <button onClick={props.state.close} type="button" class="btn-close" aria-label="Close"></button>
            </div>
            <div class="modal-body">{props.state.body({ close: props.state.close })}</div>
            {props.state.footer !== undefined ? <div class="modal-footer">{props.state.footer({ close: props.state.close })}</div> : null}
          </div>
        </UseFocusTrap>
      </div>
    </div>
  );
}

export function GaModalContainer(): GaVueComponent {
  const unpackedStore = store.unpackNull();
  return (
    <TransitionGroup name="modal">
      {unpackedStore !== null ? (
        <>
          <GaConfirmationModal state={unpackedStore.get()} key="modal" />
          <div class="modal-backdrop show" key="modal-backdrop"></div>
        </>
      ) : null}
    </TransitionGroup>
  );
}

export type ModalDialogProps = {
  title: string;
  onConfirm: () => void;
  onCancel: () => void;
  onClickOutsideDeactivates?: (e: MouseEvent | TouchEvent) => boolean;
  confirmLabel?: string;
  cancelLabel?: string;
  centered?: boolean;
};
export const GaConfirmModal = createParentComponent<ModalDialogProps>((props) => {
  const { t } = useGaContext();
  onKeyUp("Escape", () => props.onCancel());

  return (
    <div class="modal d-block" tabindex="-1" aria-modal="true" role="dialog" aria-labelledby={props.title}>
      <div class={["modal-dialog", props.centered === true ? "modal-dialog-centered" : ""]}>
        <UseFocusTrap
          options={{
            clickOutsideDeactivates: (e) => {
              if (props.onClickOutsideDeactivates !== undefined && props.onClickOutsideDeactivates(e)) {
                return true;
              } else {
                props.onCancel();
                return true;
              }
            },
          }}>
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title" id={props.title}>
                {props.title}
              </h5>
              <button onClick={props.onCancel} type="button" class="btn-close" aria-label="Close"></button>
            </div>
            <div class="modal-body">{props.children}</div>
            <div class="modal-footer">
              <GaButton onClick={props.onCancel} variant="primary-alt">
                {props.cancelLabel ?? t("general:button.cancel")}
              </GaButton>
              <GaButton onClick={props.onConfirm}>{props.confirmLabel ?? t("general:button.confirm")}</GaButton>
            </div>
          </div>
        </UseFocusTrap>
      </div>
    </div>
  );
});
